import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { DialogData, GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ScheduleControlErrorsService } from 'src/app/settings/services/schedule-control.errors.service';

/**
 * Makes easier to access the Translations Keys
 * and at same time calculate the days already selected
 */
export enum Days {
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
  Holidays = 'Holidays',
}

export type SelectedDayType = {
  [Days.Monday]: boolean;
  [Days.Tuesday]: boolean;
  [Days.Wednesday]: boolean;
  [Days.Thursday]: boolean;
  [Days.Friday]: boolean;
  [Days.Saturday]: boolean;
  [Days.Sunday]: boolean;
  [Days.Holidays]: boolean;
};

export interface ScheduleType {
  id: number;
  arm1: string;
  disarm1: string;
  arm2: string;
  disarm2: string;
  selectedDay: SelectedDayType;
  freeSlot?: { day: Days };
}

@Component({
  selector: 'app-schedule-row',
  templateUrl: './schedule-row.component.html',
  styleUrls: ['./schedule-row.component.scss'],
})
export class ScheduleRowComponent implements OnInit {
  @Input() first: boolean = false;
  @Input() last: boolean = false;
  @Input() schedule: ScheduleType;
  @Input() daysAlreadySelected: SelectedDayType;

  daysEnum = Object.values(Days);

  @Input() daysDeactivated: number[] = [];

  @Output() addChange = new EventEmitter<void>();
  @Output() deleteChange = new EventEmitter<ScheduleType>();
  @Output() scheduleChange = new EventEmitter<ScheduleType>();

  errors: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<ScheduleRowComponent>,
    private readonly translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly dialog: MatDialog,
    private readonly scheduleControlErrorsService: ScheduleControlErrorsService
  ) {
    this.scheduleControlErrorsService.errors$.subscribe((errors) => {
      if (errors) {
        if (errors?.scheduleId === this.schedule?.id) {
          this.errors = Array.from(
            new Set([...errors.fields['arm'], ...errors.fields['disarm'], ...errors.fields['days']]).values()
          );
        } else {
          this.errors = [];
        }
      }
    });
  }

  ngOnInit(): void {}

  onArmedHourChange(event: string, index: number) {
    if (index === 1) {
      this.schedule.arm1 = event;
    } else {
      this.schedule.arm2 = event;
    }
    this.scheduleChange.emit(this.schedule);
  }

  onDisarmedHourChange(event: string, index: number) {
    if (index === 1) {
      this.schedule.disarm1 = event;
    } else {
      this.schedule.disarm2 = event;
    }
    this.scheduleChange.emit(this.schedule);
  }

  onDayButtonClick(day: Days) {
    /**
     * If the day is already selected and the schedule is selected, then unselect the schedule
     */
    if (this.daysAlreadySelected[day] && !this.schedule.selectedDay[day]) {
      return;
    }

    /**
     * If its not already selected by another schedule, then select it
     */
    if (!this.daysAlreadySelected[day]) {
      this.schedule.selectedDay[day] = !this.schedule.selectedDay[day];
      this.scheduleChange.emit(this.schedule);
      return;
    }

    /**
     * If its already selected and the schedule owns the day, then un-select it as a free slot
     */
    this.schedule.selectedDay[day] = false;
    this.schedule.freeSlot = { day };
    this.scheduleChange.emit(this.schedule);
  }

  async interjectDelete() {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: {
        disableClose: true,
        autoFocus: false,
        title: this.translateService.instant('Partition.DeleteScheduleQuestion'),
        description: this.translateService.instant('Partition.DeleteScheduleDescription'),
        cancelText: this.translateService.instant('Shared.Cancel'),
        confirmText: this.translateService.instant('Shared.Delete'),
        type: 'deletionConfirmation',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteChange.emit(this.schedule);
      }
    });
  }

  showFinishedWithSuccessDialog() {
    this.dialog.open(GenericDialogComponent, {
      disableClose: true,
      autoFocus: false,
      data: {
        disableClose: true,
        autoFocus: false,
        title: this.translateService.instant('Partition.DeleteScheduleSuccess'),
        confirmText: this.translateService.instant('Shared.Ok'),
        type: 'changesSuccessfully',
        closeOnlyCurrentModal: true,
      },
    });
  }

  async onDeleteButtonClick() {
    await this.interjectDelete();
  }

  onNewScheduleButtonClick() {
    this.addChange.emit();
  }
}
